/* AOF — USP strip auto-rotating slider (mobile only).

   On <750px the four trust points become a swipeable, auto-advancing
   carousel with dots. At >=750px the layout is a static row and this
   component is inert (it stops the timer and resets the scroll).

   Accessibility:
   - prefers-reduced-motion: no autoplay (CSS also falls the layout back to
     the static 2x2 grid so all points stay visible without motion).
   - Autoplay pauses on hover / touch / when the tab is hidden, and resumes.

   Dawn-style web component; no dependencies. */
class AofUspCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-usp-track]');
    this.dotsWrap = this.querySelector('[data-usp-dots]');
    if (!this.track) return;
    this.slides = Array.from(this.track.querySelectorAll('.aof-usp__col'));
    if (this.slides.length < 2) return;

    this.interval = parseInt(this.dataset.interval, 10) || 3000;
    this.index = 0;
    this.timer = null;
    this.restartTimer = null;
    this.programmatic = false;

    this.mqMobile = window.matchMedia('(max-width: 749px)');
    this.mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.buildDots();

    this.onScroll = this.debounce(() => this.syncFromScroll(), 80);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Pause on interaction; resume a beat later.
    this.addEventListener('pointerenter', () => this.stop(), { passive: true });
    this.addEventListener('pointerleave', () => this.maybeStart(), { passive: true });
    this.addEventListener('pointerdown', () => this.stop(), { passive: true });
    this.addEventListener('touchstart', () => this.stop(), { passive: true });
    this.addEventListener('touchend', () => this.delayedStart(), { passive: true });

    this._onMq = () => this.refresh();
    this.mqMobile.addEventListener('change', this._onMq);
    this.mqReduce.addEventListener('change', this._onMq);
    this._onVis = () => { if (document.hidden) this.stop(); else this.maybeStart(); };
    document.addEventListener('visibilitychange', this._onVis);

    this.refresh();
  }

  disconnectedCallback() {
    this.stop();
    if (this.mqMobile) this.mqMobile.removeEventListener('change', this._onMq);
    if (this.mqReduce) this.mqReduce.removeEventListener('change', this._onMq);
    document.removeEventListener('visibilitychange', this._onVis);
  }

  /* Two points are visible at a time and it advances by ONE (Ben, 2026-08-28), so a
     step is the width of a SLIDE, not of the track, and the number of resting positions
     is slides - visible + 1 rather than one per slide. Both are derived at call time
     because the slide width changes with the viewport. */
  step() {
    const w = this.slides[0].getBoundingClientRect().width;
    return w > 0 ? w : this.track.clientWidth;
  }

  lastIndex() {
    const visible = Math.max(1, Math.round(this.track.clientWidth / this.step()));
    return Math.max(0, this.slides.length - visible);
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    this.dots = Array.from({ length: this.lastIndex() + 1 }, (_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'aof-usp__dot';
      b.setAttribute('aria-label', 'Show point ' + (i + 1));
      if (i === 0) b.setAttribute('aria-current', 'true');
      b.addEventListener('click', () => { this.goTo(i); this.delayedStart(); });
      this.dotsWrap.appendChild(b);
      return b;
    });
  }

  refresh() {
    if (this.mqMobile.matches) {
      this.maybeStart();
    } else {
      this.stop();
      this.track.scrollLeft = 0;
      this.index = 0;
      this.updateDots();
    }
  }

  maybeStart() {
    this.stop();
    if (this.mqMobile.matches && !this.mqReduce.matches && !document.hidden) {
      this.timer = window.setInterval(() => this.next(), this.interval);
    }
  }

  delayedStart() {
    this.stop();
    this.restartTimer = window.setTimeout(() => this.maybeStart(), 1200);
  }

  stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
  }

  next() { this.goTo(this.index >= this.lastIndex() ? 0 : this.index + 1); }

  goTo(i) {
    this.index = i;
    this.programmatic = true;
    clearTimeout(this._progT);
    this._progT = setTimeout(() => { this.programmatic = false; }, 450);
    this.track.scrollTo({
      left: i * this.step(),
      behavior: this.mqReduce.matches ? 'auto' : 'smooth',
    });
    this.updateDots();
  }

  syncFromScroll() {
    if (this.programmatic) return;
    const i = Math.round(this.track.scrollLeft / this.step());
    if (i !== this.index) { this.index = i; this.updateDots(); }
  }

  updateDots() {
    if (!this.dots) return;
    this.dots.forEach((d, i) => {
      if (i === this.index) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  }

  debounce(fn, ms) {
    let t;
    return () => { clearTimeout(t); t = setTimeout(fn, ms); };
  }
}

customElements.define('aof-usp-carousel', AofUspCarousel);
